import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Paper, Tabs, Tab } from '@mui/material';
import {
  AccountBalance as BankIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Done as DoneIcon,
  Pending as PendingIcon,
  AccountBalanceWallet as WalletIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import moment from 'moment-jalaali';
import { userWithdrawalService } from '../services/userWithdrawalService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import './EntityManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

// Money helpers with thousands separator + Persian digits
const formatMoney = (v: string | number): string => {
  const s = String(v || '').replace(/,/g, '').replace(/[^\d]/g, '');
  if (!s) return '';
  return toPersianDigits(s.replace(/\B(?=(\d{3})+(?!\d))/g, '٬'));
};

const parseMoney = (v: string): string => {
  return v.replace(/[٬,]/g, '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
};

const formatJalaliShort = (iso: string): string => {
  if (!iso) return '';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD')); }
  catch { return iso; }
};

interface WithdrawalRequest {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'cancelled';
  bankAccountInfo: {
    bankName: string;
    accountNumber: string;
    iban: string;
    accountHolderName: string;
    description?: string;
  };
  requestDate: string;
  processedDate?: string;
  processedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  adminNote?: string;
  rejectReason?: string;
  paymentReference?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface WithdrawalStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  processingRequests?: number;
  completedRequests?: number;
  rejectedRequests: number;
  cancelledRequests?: number;
  totalAmount: number;
  pendingAmount: number;
  reservedAmount?: number;
  completedAmount?: number;
}

type TabValue = 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'cancelled';

const UserWithdrawalManagement: React.FC = () => {
  // Data
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WithdrawalStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tab
  const [tabValue, setTabValue] = useState<TabValue>('pending');

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [filtersApplied, setFiltersApplied] = useState(0);
  // فیلترهای اعمال‌شده (سمت سرور) — با «اعمال» از ورودی‌ها کپی می‌شود
  const [applied, setApplied] = useState<{ search: string; minAmount: string; maxAmount: string; bankName: string; verified: 'all' | 'verified' | 'unverified' }>({ search: '', minAmount: '', maxAmount: '', bankName: '', verified: 'all' });
  const [actionMode, setActionMode] = useState<'reject' | 'fail'>('reject');

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequest | null>(null);

  // Form states
  const [adminNote, setAdminNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await userWithdrawalService.getAllWithdrawalRequests(page, rowsPerPage, tabValue, applied);
      const data: any = result.data || {};
      setRequests(data.requests || []);
      // Backend returns pagination as data.pagination.{pages,total} (wallet.controller),
      // older code returned data.{totalPages,totalCount} — accept both.
      const pages = data.pagination?.pages ?? data.totalPages ?? 1;
      const total = data.pagination?.total ?? data.totalCount ?? 0;
      setTotalPages(pages || 1);
      setTotalCount(total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, tabValue, applied]);

  const loadStats = useCallback(async () => {
    try {
      const result = await userWithdrawalService.getWithdrawalStatistics();
      setStats(result.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handleRefresh = () => {
    setPage(1);
    loadData();
    loadStats();
  };

  const handleSearch = () => {
    let count = 0;
    if (searchInput) count++;
    if (minAmount) count++;
    if (maxAmount) count++;
    if (bankFilter) count++;
    if (verifiedFilter !== 'all') count++;
    setFiltersApplied(count);
    setPage(1);
    setApplied({ search: searchInput, minAmount, maxAmount, bankName: bankFilter, verified: verifiedFilter });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchInput('');
    setMinAmount('');
    setMaxAmount('');
    setBankFilter('');
    setVerifiedFilter('all');
    setFiltersApplied(0);
    setPage(1);
    setApplied({ search: '', minAmount: '', maxAmount: '', bankName: '', verified: 'all' });
  };

  // ✅ فیلترها سمت سرور اعمال می‌شوند (قبلاً فقط ۲۰ ردیف صفحهٔ فعلی فیلتر می‌شد و شمارندهٔ «نمایش x از y» غلط بود)
  const filteredRequests = requests;

  // Dialog handlers
  const handleViewDetails = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleApproveClick = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setAdminNote('');
    setApproveDialogOpen(true);
  };

  const handleProcessClick = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setAdminNote('');
    setPaymentReference(request.paymentReference || '');
    setProcessDialogOpen(true);
  };

  const handleCompleteClick = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setAdminNote('');
    setPaymentReference(request.paymentReference || '');
    setCompleteDialogOpen(true);
  };

  const handleRejectClick = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setRejectReason('');
    setAdminNote('');
    setActionMode('reject');
    setRejectDialogOpen(true);
  };

  /** ناموفق/لغو بعد از تأیید (واریز بانکی انجام نشد) — پول به کیف پول کاربر برمی‌گردد */
  const handleFailClick = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setRejectReason('');
    setAdminNote('');
    setActionMode('fail');
    setRejectDialogOpen(true);
  };

  // Action handlers
  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await userWithdrawalService.approveWithdrawalRequest(selectedRequest._id, adminNote);
      setApproveDialogOpen(false);
      loadData();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تایید درخواست');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartProcessing = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await userWithdrawalService.startProcessingWithdrawal(
        selectedRequest._id,
        adminNote,
        paymentReference.trim() || undefined
      );
      setProcessDialogOpen(false);
      loadData();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در شروع پردازش');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await userWithdrawalService.completeWithdrawal(
        selectedRequest._id,
        adminNote,
        paymentReference.trim() || undefined
      );
      setCompleteDialogOpen(false);
      loadData();
      loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در تکمیل برداشت');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) return;
    if (rejectReason.trim().length < 5) { setError('دلیل باید حداقل ۵ حرف باشد'); return; }
    try {
      setActionLoading(true);
      if (actionMode === 'fail') await userWithdrawalService.failWithdrawal(selectedRequest._id, rejectReason.trim(), adminNote || undefined);
      else await userWithdrawalService.rejectWithdrawalRequest(selectedRequest._id, rejectReason.trim(), adminNote || undefined);
      setRejectDialogOpen(false);
      loadData();
      loadStats();
    } catch (err: any) {
      const errs = err.response?.data?.errors;
      setError((Array.isArray(errs) && errs[0]?.message) || err.response?.data?.message || err?.message || (actionMode === 'fail' ? 'خطا در ثبت ناموفق' : 'خطا در رد درخواست'));
    } finally {
      setActionLoading(false);
    }
  };

  // Formatters
  const formatPrice = (amount: number | undefined): string =>
    toPersianDigits((amount || 0).toLocaleString('en-US'));

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    return toPersianDigits(moment(dateString).format('jYYYY/jMM/jDD HH:mm'));
  };

  const getStatusText = (status: string): string => {
    const map: Record<string, string> = {
      pending: 'در انتظار',
      approved: 'تایید شده',
      rejected: 'رد شده',
      processing: 'در حال پردازش',
      completed: 'تکمیل شده',
      cancelled: 'لغو شده'
    };
    return map[status] || status;
  };

  const getStatusTag = (status: string) => {
    let cls = 'tag-warning';
    switch (status) {
      case 'completed':
      case 'approved':
        cls = 'tag-success';
        break;
      case 'rejected':
        cls = 'tag-danger';
        break;
      case 'processing':
        cls = 'tag-info';
        break;
      case 'cancelled':
        cls = 'tag-default';
        break;
      case 'pending':
      default:
        cls = 'tag-warning';
    }
    return <span className={`tag tag-sm ${cls}`}>{getStatusText(status)}</span>;
  };

  // Stats aggregation
  const totalStats = stats?.totalRequests || 0;
  const pendingStats = stats?.pendingRequests || 0;
  // «تایید/تکمیل شده» = تأییدشده + در حال پردازش + تکمیل‌شده (قبلاً فقط APPROVED شمرده می‌شد)
  const approvedStats = (stats?.approvedRequests || 0) + (stats?.processingRequests || 0) + (stats?.completedRequests || 0);
  const rejectedStats = (stats?.rejectedRequests || 0) + (stats?.cancelledRequests || 0);

  const startItem = totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);
  const hasActiveFilters = filtersApplied > 0;

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="user_withdrawal" />

      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">درخواست‌های برداشت کاربران</h1>
          <p className="entity-subtitle">تأیید و پرداخت درخواست‌های برداشت کاربران</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn refresh"
            onClick={handleRefresh}
            disabled={loading}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div
          style={{
            margin: '0 0 12px',
            padding: '12px 16px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: 8,
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b' }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="entity-stats">
          <div className="entity-stat-card">
            <div className="entity-stat-icon blue"><WalletIcon /></div>
            <div>
              <p className="entity-stat-label">کل درخواست‌ها</p>
              <p className="entity-stat-value">{toPersianDigits(totalStats.toLocaleString('en-US'))}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon orange"><PendingIcon /></div>
            <div>
              <p className="entity-stat-label">در انتظار تایید</p>
              <p className="entity-stat-value">{toPersianDigits(pendingStats.toLocaleString('en-US'))}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon green"><CheckIcon /></div>
            <div>
              <p className="entity-stat-label">تایید/تکمیل شده</p>
              <p className="entity-stat-value">{toPersianDigits(approvedStats.toLocaleString('en-US'))}</p>
            </div>
          </div>
          <div className="entity-stat-card">
            <div className="entity-stat-icon red"><CancelIcon /></div>
            <div>
              <p className="entity-stat-label">رد/ناموفق</p>
              <p className="entity-stat-value">{toPersianDigits(rejectedStats.toLocaleString('en-US'))}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: '12px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ px: 2, borderBottom: '1px solid var(--color-border)' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="در انتظار" value="pending" />
          <Tab label="تایید شده" value="approved" />
          <Tab label="در حال پردازش" value="processing" />
          <Tab label="تکمیل شده" value="completed" />
          <Tab label="رد شده" value="rejected" />
          <Tab label="لغو شده" value="cancelled" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="نام، ایمیل، صاحب حساب..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">حداقل مبلغ (تومان)</label>
            <input
              className="filter-field"
              type="text"
              inputMode="numeric"
              value={formatMoney(minAmount)}
              onChange={(e) => setMinAmount(parseMoney(e.target.value))}
              placeholder="مثلا ۵۰٬۰۰۰"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">حداکثر مبلغ (تومان)</label>
            <input
              className="filter-field"
              type="text"
              inputMode="numeric"
              value={formatMoney(maxAmount)}
              onChange={(e) => setMaxAmount(parseMoney(e.target.value))}
              placeholder="مثلا ۱٬۰۰۰٬۰۰۰"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">نام بانک</label>
            <input
              className="filter-field"
              type="text"
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
              placeholder="مثلا ملت"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت تایید</label>
            <select
              className="filter-field"
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value as any)}
            >
              <option value="all">همه</option>
              <option value="verified">تایید شده</option>
              <option value="unverified">تایید نشده</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchInput && (
              <span className="filter-tag">
                جستجو: {searchInput}
                <button onClick={() => { setSearchInput(''); handleSearch(); }}>×</button>
              </span>
            )}
            {minAmount && (
              <span className="filter-tag">
                حداقل: {formatMoney(minAmount)} تومان
                <button onClick={() => { setMinAmount(''); handleSearch(); }}>×</button>
              </span>
            )}
            {maxAmount && (
              <span className="filter-tag">
                حداکثر: {formatMoney(maxAmount)} تومان
                <button onClick={() => { setMaxAmount(''); handleSearch(); }}>×</button>
              </span>
            )}
            {bankFilter && (
              <span className="filter-tag">
                بانک: {bankFilter}
                <button onClick={() => { setBankFilter(''); handleSearch(); }}>×</button>
              </span>
            )}
            {verifiedFilter !== 'all' && (
              <span className="filter-tag">
                {verifiedFilter === 'verified' ? 'تایید شده' : 'تایید نشده'}
                <button onClick={() => { setVerifiedFilter('all'); handleSearch(); }}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : filteredRequests.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><WalletIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">درخواست برداشتی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>شناسه</th>
                  <th>کاربر</th>
                  <th>مبلغ</th>
                  <th>بانک</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تایید</th>
                  <th>تاریخ درخواست</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {toPersianDigits(r._id.slice(-8))}
                      </span>
                    </td>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-info">
                          <span className="entity-name">{r.userId?.name || 'نامشخص'}</span>
                          <span className="entity-desc">{r.userId?.email || ''}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{formatPrice(r.amount)} تومان</span>
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{r.bankAccountInfo?.bankName || '-'}</span>
                        <span className="entity-desc">{r.bankAccountInfo?.accountHolderName || ''}</span>
                      </div>
                    </td>
                    <td>{getStatusTag(r.status)}</td>
                    <td><AssigneeCell value={(r as any).assignedTo} /></td>
                    <td>
                      <span className={`tag tag-sm ${r.isVerified ? 'tag-success' : 'tag-warning'}`}>
                        {r.isVerified ? 'تایید شده' : 'تایید نشده'}
                      </span>
                    </td>
                    <td>
                      <span className="entity-desc">{formatDate(r.requestDate)}</span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => handleViewDetails(r)}>
                            <ViewIcon />
                          </button>
                        </Tooltip>
                        {r.status === 'pending' && r.isVerified && (
                          <Tooltip title="تایید" arrow>
                            <button
                              className="action-btn toggle active"
                              onClick={() => handleApproveClick(r)}
                            >
                              <CheckIcon />
                            </button>
                          </Tooltip>
                        )}
                        {r.status === 'pending' && (
                          <Tooltip title={r.isVerified ? 'رد' : 'رد (کاربر OTP را تایید نکرده)'} arrow>
                            <button
                              className="action-btn delete"
                              onClick={() => handleRejectClick(r)}
                            >
                              <CancelIcon />
                            </button>
                          </Tooltip>
                        )}
                        {r.status === 'approved' && (
                          <Tooltip title="شروع پردازش" arrow>
                            <button
                              className="action-btn edit"
                              onClick={() => handleProcessClick(r)}
                            >
                              <PlayIcon />
                            </button>
                          </Tooltip>
                        )}
                        {r.status === 'processing' && (
                          <Tooltip title="تکمیل" arrow>
                            <button
                              className="action-btn toggle active"
                              onClick={() => handleCompleteClick(r)}
                            >
                              <DoneIcon />
                            </button>
                          </Tooltip>
                        )}
                        {(r.status === 'approved' || r.status === 'processing') && (
                          <Tooltip title="ناموفق / لغو (واریز انجام نشد — پول به کیف پول برمی‌گردد)" arrow>
                            <button
                              className="action-btn delete"
                              onClick={() => handleFailClick(r)}
                            >
                              <CancelIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {filteredRequests.map((r) => (
                <div key={r._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image"><WalletIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">
                        {r.userId?.name || 'نامشخص'}
                      </div>
                      <div className="entity-card-desc">
                        {r.bankAccountInfo?.bankName || '-'}
                      </div>
                      <div className="entity-card-desc">
                        {formatPrice(r.amount)} تومان
                      </div>
                    </div>
                    <div className="entity-card-status">{getStatusTag(r.status)}</div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleViewDetails(r)}>
                      <ViewIcon fontSize="small" /> جزئیات
                    </button>
                    {r.status === 'pending' && r.isVerified && (
                      <button className="entity-card-btn" onClick={() => handleApproveClick(r)}>
                        <CheckIcon fontSize="small" /> تایید
                      </button>
                    )}
                    {r.status === 'pending' && (
                      <button className="entity-card-btn" onClick={() => handleRejectClick(r)}>
                        <CancelIcon fontSize="small" /> {r.isVerified ? 'رد' : 'رد (تایید نشده)'}
                      </button>
                    )}
                    {r.status === 'approved' && (
                      <button className="entity-card-btn" onClick={() => handleProcessClick(r)}>
                        <PlayIcon fontSize="small" /> پردازش
                      </button>
                    )}
                    {r.status === 'processing' && (
                      <button className="entity-card-btn" onClick={() => handleCompleteClick(r)}>
                        <DoneIcon fontSize="small" /> تکمیل
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalCount))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(String(page))} / {toPersianDigits(String(totalPages))}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        className="userwithdrawal-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات درخواست برداشت کاربر</span>
          <button className="details-close" onClick={() => setDetailDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedRequest && (
            <div className="edit-form">
              {/* Request info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات درخواست</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>شناسه</label>
                      <input
                        type="text"
                        value={selectedRequest._id}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    <div className="edit-field">
                      <label>وضعیت</label>
                      <div style={{ paddingTop: 4 }}>{getStatusTag(selectedRequest.status)}</div>
                    </div>
                    <div className="edit-field">
                      <label>وضعیت تایید</label>
                      <div style={{ paddingTop: 4 }}>
                        <span className={`tag tag-sm ${selectedRequest.isVerified ? 'tag-success' : 'tag-warning'}`}>
                          {selectedRequest.isVerified ? 'تایید شده' : 'تایید نشده'}
                        </span>
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ درخواست</label>
                      <input type="text" value={formatDate(selectedRequest.requestDate)} readOnly />
                    </div>
                    {selectedRequest.processedDate && (
                      <div className="edit-field">
                        <label>تاریخ پردازش</label>
                        <input type="text" value={formatDate(selectedRequest.processedDate)} readOnly />
                      </div>
                    )}
                    <div className="edit-field">
                      <label>تاریخ ایجاد</label>
                      <input type="text" value={formatDate(selectedRequest.createdAt)} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ بروزرسانی</label>
                      <input type="text" value={formatDate(selectedRequest.updatedAt)} readOnly />
                    </div>
                  </div>
                </div>
              </div>

              {/* User info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات کاربر</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام کاربر</label>
                      <input type="text" value={selectedRequest.userId?.name || '-'} readOnly />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input type="text" value={selectedRequest.userId?.email || '-'} readOnly />
                    </div>
                    <div className="edit-field full">
                      <label>شناسه کاربر</label>
                      <input
                        type="text"
                        value={selectedRequest.userId?._id || '-'}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Amount/Fee */}
              <div className="edit-card">
                <div className="edit-card-header">مبلغ و کارمزد</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>مبلغ درخواستی</label>
                      <input
                        type="text"
                        value={`${formatPrice(selectedRequest.amount)} تومان`}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات بانکی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام بانک</label>
                      <input
                        type="text"
                        value={selectedRequest.bankAccountInfo?.bankName || '-'}
                        readOnly
                      />
                    </div>
                    <div className="edit-field">
                      <label>نام صاحب حساب</label>
                      <input
                        type="text"
                        value={selectedRequest.bankAccountInfo?.accountHolderName || '-'}
                        readOnly
                      />
                    </div>
                    <div className="edit-field full">
                      <label>شماره شبا</label>
                      <input
                        type="text"
                        value={selectedRequest.bankAccountInfo?.iban || '-'}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    <div className="edit-field full">
                      <label>شماره حساب</label>
                      <input
                        type="text"
                        value={selectedRequest.bankAccountInfo?.accountNumber || '-'}
                        readOnly
                        style={{ fontFamily: 'monospace' }}
                      />
                    </div>
                    {selectedRequest.bankAccountInfo?.description && (
                      <div className="edit-field full">
                        <label>توضیحات بانکی</label>
                        <textarea
                          value={selectedRequest.bankAccountInfo.description}
                          readOnly
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status & History */}
              {(selectedRequest.rejectReason || selectedRequest.adminNote || selectedRequest.processedBy) && (
                <div className="edit-card">
                  <div className="edit-card-header">وضعیت و تاریخچه</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      {selectedRequest.processedBy && (
                        <>
                          <div className="edit-field">
                            <label>پردازش‌کننده</label>
                            <input
                              type="text"
                              value={selectedRequest.processedBy.name || '-'}
                              readOnly
                            />
                          </div>
                          <div className="edit-field">
                            <label>ایمیل پردازش‌کننده</label>
                            <input
                              type="text"
                              value={selectedRequest.processedBy.email || '-'}
                              readOnly
                            />
                          </div>
                        </>
                      )}
                      {selectedRequest.rejectReason && (
                        <div className="edit-field full">
                          <label>دلیل رد</label>
                          <textarea value={selectedRequest.rejectReason} readOnly rows={2} />
                        </div>
                      )}
                      {selectedRequest.adminNote && (
                        <div className="edit-field full">
                          <label>یادداشت ادمین</label>
                          <textarea value={selectedRequest.adminNote} readOnly rows={3} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setDetailDialogOpen(false)}>بستن</button>
        </div>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !actionLoading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="userwithdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>تایید درخواست برداشت</span>
          <button
            className="details-close"
            onClick={() => !actionLoading && setApproveDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تایید</div>
              <div className="edit-card-body">
                {selectedRequest && (
                  <p
                    style={{
                      marginTop: 0,
                      padding: 12,
                      background: 'rgba(37, 99, 235, 0.08)',
                      borderRadius: 8,
                      fontSize: '0.875rem'
                    }}
                  >
                    آیا از تایید درخواست برداشت {formatPrice(selectedRequest.amount)} تومان اطمینان دارید؟ درخواست آماده پردازش خواهد شد.
                  </p>
                )}
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="توضیحات اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button
            className="edit-btn cancel"
            onClick={() => setApproveDialogOpen(false)}
            disabled={actionLoading}
          >
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleApprove}
            disabled={actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            تایید درخواست
          </button>
        </div>
      </Dialog>

      {/* Process Dialog */}
      <Dialog
        open={processDialogOpen}
        onClose={() => !actionLoading && setProcessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="userwithdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>شروع پردازش برداشت</span>
          <button
            className="details-close"
            onClick={() => !actionLoading && setProcessDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">پردازش</div>
              <div className="edit-card-body">
                {selectedRequest && (
                  <p
                    style={{
                      marginTop: 0,
                      padding: 12,
                      background: 'rgba(37, 99, 235, 0.08)',
                      borderRadius: 8,
                      fontSize: '0.875rem'
                    }}
                  >
                    آیا از شروع پردازش برداشت {formatPrice(selectedRequest.amount)} تومان اطمینان دارید؟ مبلغ قبلاً هنگام تأیید پیامکی از موجودی کاربر رزرو شده و اکنون به «برداشت‌شده» منتقل می‌شود.
                  </p>
                )}
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>کد رهگیری تراکنش (اختیاری)</label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="مثال: 123456789"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="توضیحات اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button
            className="edit-btn cancel"
            onClick={() => setProcessDialogOpen(false)}
            disabled={actionLoading}
          >
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleStartProcessing}
            disabled={actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            شروع پردازش
          </button>
        </div>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => !actionLoading && setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="userwithdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>تکمیل برداشت</span>
          <button
            className="details-close"
            onClick={() => !actionLoading && setCompleteDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">تکمیل</div>
              <div className="edit-card-body">
                {selectedRequest && (
                  <p
                    style={{
                      marginTop: 0,
                      padding: 12,
                      background: 'rgba(37, 99, 235, 0.08)',
                      borderRadius: 8,
                      fontSize: '0.875rem'
                    }}
                  >
                    مبلغ {formatPrice(selectedRequest.amount)} تومان به حساب کاربر واریز خواهد شد.
                  </p>
                )}
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>کد رهگیری تراکنش (اختیاری)</label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="مثال: 123456789"
                      disabled={actionLoading}
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="توضیحات اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button
            className="edit-btn cancel"
            onClick={() => setCompleteDialogOpen(false)}
            disabled={actionLoading}
          >
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleComplete}
            disabled={actionLoading}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            تکمیل برداشت
          </button>
        </div>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !actionLoading && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="userwithdrawal-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>{actionMode === 'fail' ? 'ناموفق / لغو برداشت (برگشت پول به کیف پول)' : 'رد درخواست برداشت'}</span>
          <button
            className="details-close"
            onClick={() => !actionLoading && setRejectDialogOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">رد درخواست</div>
              <div className="edit-card-body">
                {selectedRequest && (
                  <p
                    style={{
                      marginTop: 0,
                      padding: 12,
                      background: 'rgba(220, 38, 38, 0.08)',
                      borderRadius: 8,
                      fontSize: '0.875rem'
                    }}
                  >
                    {actionMode === 'fail'
                      ? `برداشت ${formatPrice(selectedRequest.amount)} تومان ناموفق ثبت می‌شود و مبلغ به موجودی کیف پول کاربر برمی‌گردد.`
                      : `درخواست برداشت ${formatPrice(selectedRequest.amount)} تومان رد خواهد شد.`}
                  </p>
                )}
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>{actionMode === 'fail' ? 'دلیل ناموفق‌بودن' : 'دلیل رد'} <span style={{ color: '#dc2626' }}>*</span> <span style={{ fontSize: 11, color: '#6b7280' }}>(۵ تا ۲۰۰ حرف)</span></label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="دلیل رد درخواست را بنویسید..."
                      rows={2}
                      disabled={actionLoading}
                      required
                    />
                  </div>
                  <div className="edit-field full">
                    <label>یادداشت ادمین (اختیاری)</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="توضیحات اضافی..."
                      rows={3}
                      disabled={actionLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button
            className="edit-btn cancel"
            onClick={() => setRejectDialogOpen(false)}
            disabled={actionLoading}
          >
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleReject}
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            رد درخواست
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default UserWithdrawalManagement;
